import React from 'react'
import { Link } from 'react-router-dom'

import {
  Button,
  Flex,
  CTABanner,
  ApprovedIcon,
  DeniedIcon,
  PendingIcon,
  ExpireIcon,
  P,
} from '../../lib/components'
import { KYCPresenterProps } from '../../lib/types'
import { CONTACT_US, GET_IN_TOUCH_WITH_ADMIN, QUESTION_FOR_KYC } from '../../lib/utils/message'
import { ContactUsModal } from '../../lib/components/ContactUsModal'
import { SumSub } from './kyc-sumsub'
// REMOVED: SelfDeclarationSigning import - now clubbed with client agreement
import { ClientAgreementSigning } from './client-agreement-signing'
import './kyc.scss'

const KYCPresenter = ({
  kycStatusData,
  getKYC,
  noOfRejectedKyc,
  handleClickModal,
  profileTab,
  accountStatus,
  MAX_RETRY,
  showContactUsModal,
  handleSelfDeclarationSigned,
  // REMOVED: showSelfDeclarationSigning - now clubbed with client agreement
  handleClientAgreementSigned,
  showClientAgreementSigning,
  portfolioIds,
  selfDeclarationSigned,
  clientAgreementSigned,
}: KYCPresenterProps) => {
  const getPortfolioIdsBasedonStatus = (kycStatus: string) => {
    return portfolioIds
      ?.filter((item) => item.status === kycStatus)
      .map((item) => item.investor_portfolio_id)
  }

  return (
    <>
      <div className={profileTab === 'kyc' ? 'kyc-container-profile' : 'kyc-container'}>
        {profileTab === 'kyc' && (
          <div className='kyc-status-container'>
            <span className='kyc-status-container-label'>Your current status</span>
            <span
              className={`kyc-status-container-status-${
                kycStatusData === 'RESUBMIT_SOF' ? 'RESUBMIT' : kycStatusData
              }`}
              data-testid='kyc-status-value'
            >
              {accountStatus === 'DORMANT' ? (
                <>
                  <PendingIcon height='21' width='21' /> Your Account is Dormant
                </>
              ) : (
                <>
                  {kycStatusData === 'NOT_STARTED' && (
                    <>
                      <PendingIcon height='21' width='21' /> Not Started
                    </>
                  )}
                  {kycStatusData === 'INITIATED' && (
                    <>
                      <PendingIcon height='21' width='21' /> Initiated
                    </>
                  )}
                  {kycStatusData === 'PENDING' && (
                    <>
                      <PendingIcon height='21' width='21' /> Sent For Approval
                    </>
                  )}
                  {kycStatusData === 'REJECTED' && (
                    <>
                      <DeniedIcon height='21' width='21' /> Rejected
                    </>
                  )}
                  {kycStatusData === 'RESUBMIT' && (
                    <>
                      <DeniedIcon height='21' width='21' /> Resubmit
                    </>
                  )}
                  {kycStatusData === 'RESUBMIT_SOF' && (
                    <>
                      <DeniedIcon height='21' width='21' /> Resubmit Source of Funds
                    </>
                  )}
                  {kycStatusData === 'APPROVED' && (
                    <>
                      <ApprovedIcon height='21' width='21' /> Approved
                    </>
                  )}
                  {kycStatusData === 'EXPIRED' && (
                    <>
                      <ExpireIcon height='22' width='22' /> Expired
                    </>
                  )}
                </>
              )}
            </span>
            <span>
              {/* Current Portfolio ID for Approved KYC*/}
              {kycStatusData === 'APPROVED' &&
                getPortfolioIdsBasedonStatus('APPROVED').length > 0 && (
                  <Flex classes={['mt-16']}>
                    <P text='Portfolio ID:' classes={['mr-13', 'bold']} />
                    <P text={getPortfolioIdsBasedonStatus('APPROVED')?.[0]} />
                  </Flex>
                )}

              {/* Expired KYC */}
              {kycStatusData === 'EXPIRED' &&
                getPortfolioIdsBasedonStatus('EXPIRED').length > 0 && (
                  <Flex classes={['mt-16']}>
                    <P text='Old Portfolio ID:' classes={['mr-13', 'bold']} />
                    <P text={getPortfolioIdsBasedonStatus('EXPIRED')?.[0]} />
                  </Flex>
                )}

              {/* Starting KYC again after previous was expired */}
              {kycStatusData === 'NOT_STARTED' &&
                getPortfolioIdsBasedonStatus('EXPIRED').length > 0 && (
                  <Flex classes={['mt-16']}>
                    <P text='Old Portfolio ID:' classes={['mr-13', 'bold']} />
                    <P text={getPortfolioIdsBasedonStatus('EXPIRED')?.[0]} />
                  </Flex>
                )}

              {/* Intermediary KYC states after previous KYC was expired */}
              {['INITIATED', 'PENDING', 'REJECTED', 'RESUBMIT', 'RESUBMIT_SOF'].includes(
                kycStatusData ?? '',
              ) &&
                getPortfolioIdsBasedonStatus('EXPIRED').length > 0 && (
                  <Flex classes={['mt-16']}>
                    <P text='Old Portfolio ID:' classes={['mr-13', 'bold']} />
                    <P text={getPortfolioIdsBasedonStatus('EXPIRED')?.[0]} />
                  </Flex>
                )}
            </span>
          </div>
        )}
        <div className='kyc-heading'>
          {kycStatusData === 'APPROVED' && clientAgreementSigned && (
            <div className='kyc-sub-heading-text-container-note'>
              <p className='basic kyc-sub-heading-text-note'>
                You can now go ahead to browse through{' '}
                <Link className='kyc-link' to={'/opportunities'}>
                  opportunities
                </Link>
                .
              </p>
            </div>
          )}
          {!clientAgreementSigned && (
            <div
              className='kyc-sub-heading-text-container-note'
              style={{ backgroundColor: '#fff3cd', borderColor: '#ffeaa7' }}
            >
              <p className='basic kyc-sub-heading-text-note' style={{ color: '#856404' }}>
                Please complete the Client Agreement & Self-Declaration signing before starting KYC
                and investing in opportunities.
              </p>
            </div>
          )}
          {kycStatusData === 'REJECTED' && noOfRejectedKyc >= MAX_RETRY && (
            <div className='kyc-sub-heading-text-container' data-testid='kyc-text-maximum-tries'>
              <p className='kyc-sub-heading-text-disclaimer'>
                You have reached the maximum number of retries for KYC. Please contact the Credibila
                team at support@credibila.com.
              </p>
            </div>
          )}
          {((kycStatusData === 'REJECTED' && noOfRejectedKyc < MAX_RETRY) ||
            kycStatusData === 'RESUBMIT') && (
            <div className='kyc-sub-heading-text-container' data-testid='kyc-text-retry'>
              <p className='kyc-sub-heading-text-disclaimer'>
                We are unable to complete your KYC verification due to issues encountered during the
                process.
              </p>
              <p className='kyc-sub-heading-text-disclaimer'>This could be because:</p>
              <ol className='ml-14'>
                <li>Our system was unable to automatically verify your details.</li>
                <li>Our compliance team manually rejected your KYC after review.</li>
              </ol>
              <p className='kyc-sub-heading-text-disclaimer mt-14'>
                Please ensure all submitted details and documents are accurate and meet the
                necessary requirements. If you believe this was a mistake or need further
                assistance, please contact our support team.
              </p>
            </div>
          )}
          {kycStatusData === 'RESUBMIT_SOF' && (
            <div className='kyc-sub-heading-text-container' data-testid='kyc-text-resubmit-sof'>
              <p className='kyc-sub-heading-text-disclaimer'>
                We require additional information about your source of funds to update your
                investment limit in compliance with regulatory requirements. Please resubmit your
                source of funds documentation.
              </p>
              <p className='kyc-sub-heading-text-disclaimer mt-14'>Once received, our team will:</p>
              <ol className='ml-14'>
                <li>Review the documents</li>
                <li>Process your investment limit extension request</li>
              </ol>
              <p className='kyc-sub-heading-text-disclaimer mt-14'>
                Ensure the documents clearly show the source of your investment funds. For
                assistance, contact our{' '}
                <span className='kyc-link cursor-pointer' onClick={handleClickModal}>
                  support team
                </span>
                .
              </p>
            </div>
          )}
        </div>

        <SumSub
          kycStatus={kycStatusData}
          noOfRejectedKyc={noOfRejectedKyc}
          getKYC={getKYC}
          clientAgreementSigned={clientAgreementSigned}
        />

        <div className='kyc-sub-heading-text-container-note'>
          <p className='basic kyc-sub-heading-text-note mb-10'>
            Note: Your account will be considered dormant until your first investment is made
          </p>
          <ul className='ml-24 basic'>
            <li>
              If your account remains dormant for more than <strong>6 months</strong>, it will be
              closed automatically.
            </li>
            <li>
              In the event you have made an initial investment and later redeemed your entire
              portfolio, your account will enter a dormancy mode. If there is no activity in the
              account for an additional <strong>6 months</strong>, the portfolio account will be
              closed.
            </li>
          </ul>
        </div>
        <div className='kyc-sub-heading-text-container-note'>
          <p className='basic kyc-sub-heading-text-note'>
            Note: We currently accept individual clients only. If you are a corporate entity or
            institution, please contact your relationship manager or reach out to us via the Contact
            Us button below.
          </p>
        </div>

        <div className='kyc-footer'>
          <Flex variant='row-center' classes={['mt-10']}>
            <CTABanner
              heading={QUESTION_FOR_KYC}
              subHeading={GET_IN_TOUCH_WITH_ADMIN}
              ButtonComponent={
                <Button
                  classes={['auto-btn-width']}
                  variant='primary-btn'
                  text={CONTACT_US}
                  handleClick={handleClickModal}
                />
              }
            />
          </Flex>
          {showContactUsModal ? (
            <ContactUsModal active={true} subject={'General'} handleOnClose={handleClickModal} />
          ) : null}
        </div>
      </div>

      {/* REMOVED: SelfDeclarationSigning component - now clubbed with client agreement */}

      {showClientAgreementSigning && (
        <ClientAgreementSigning onSigned={handleClientAgreementSigned} />
      )}
    </>
  )
}

export { KYCPresenter }
